import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, inject } from '@angular/core';
import { TileService } from '../../services/tile.service';
import { CurrentWeather, Tile, TileWidget } from '../../types/tile.types';
import { weatherCondition } from './home-widget.util';

type WeatherWidget = Extract<TileWidget, { type: 'weather' }>;

/** Alle 20 Minuten neu laden, so lange speichert auch der Server zwischen. */
const REFRESH_MS = 20 * 60 * 1000;

/** Wetter-Kachel für eine Postleitzahl (Daten von Open-Meteo über /api/tile/weather). */
@Component({
  selector: 'app-weather-tile',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./home-widget.scss'],
  template: `
    <div class="widget-card weather-card" [style.--tile-accent]="tile.color">
      <div class="widget-head">
        <i class="ri-map-pin-line" aria-hidden="true"></i>
        <span class="widget-title">{{ tile.name }}</span>
      </div>
      <div class="widget-body">
        @if (weather; as w) {
        <div class="weather-now">
          <i class="weather-icon" [class]="condition.icon" aria-hidden="true"></i>
          <span class="weather-temp">{{ round(w.temperature) }}°</span>
        </div>
        <div class="weather-label">{{ condition.label }}</div>
        <div class="widget-meta">H {{ round(w.temperatureMax) }}° · T {{ round(w.temperatureMin) }}° · {{ w.place }}</div>
        } @else if (error) {
        <div class="widget-meta" role="alert">{{ error }}</div>
        } @else {
        <div class="widget-meta">Wetter wird geladen …</div>
        }
      </div>
    </div>
  `,
})
export class WeatherTileComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) public tile!: Tile;
  public weather?: CurrentWeather;
  public condition = { label: '', icon: '' };
  public error = '';
  private postalCode = '';
  private timer?: ReturnType<typeof setInterval>;
  private readonly tiles = inject(TileService);
  private readonly cdr = inject(ChangeDetectorRef);

  public round(value: number): number {
    return Math.round(value);
  }

  public ngOnChanges(): void {
    const postalCode = (this.tile.widget as WeatherWidget).postalCode;
    if (postalCode === this.postalCode) return;
    this.postalCode = postalCode;
    this.weather = undefined;
    this.load();
    clearInterval(this.timer);
    this.timer = setInterval(() => this.load(), REFRESH_MS);
  }

  public ngOnDestroy(): void {
    clearInterval(this.timer);
  }

  private async load(): Promise<void> {
    const postalCode = this.postalCode;
    try {
      const weather = await this.tiles.getWeather(postalCode);
      if (postalCode !== this.postalCode) return;
      this.weather = weather;
      this.condition = weatherCondition(weather.weatherCode, weather.isDay);
      this.error = '';
    } catch {
      // Bei einem Fehler bleibt ein bereits geladener Stand stehen.
      if (!this.weather) this.error = 'Das Wetter ist gerade nicht verfügbar.';
    }
    this.cdr.markForCheck();
  }
}
